/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from "@ember/object";
import type RouterService from "@ember/routing/router-service";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import type { BufferedChangeset } from "ember-changeset/types";
import $ from "jquery";
import type { ForcBaseArgs } from "parog-web/components/forc/abstract/base-component";
import CrudJournalEventBaseComponent from "parog-web/components/forc/abstract/crud-journal-event-based-component";
import type { ToastMessage } from "parog-web/components/forc/abstract/journal-event-based-component";
import DraftManager from "parog-web/components/forc/draft-manager";
import BioExamResultsReceivedJournalEvent from "parog-web/models/bio-exam-results-received-journal-event";
import type BioExamObservation from "parog-web/models/loinc";
import type Patient from "parog-web/models/patient";
import type SemeiaSessionService from "parog-web/services/session";
import type { ExamsByGroup } from "../utils/groupedBioExams";
import { getGroupedBioExams } from "../utils/groupedBioExams";
import bioExamResultsFilter from "./bio-exam-results-config-filter";

import styles from "./index.module.css";

interface Args extends ForcBaseArgs {
  changeset: BufferedChangeset;
  model: BioExamResultsReceivedJournalEvent;
  patient: Patient;
}

interface HTMLSidebarElement extends JQuery {
  sidebar: (command: string) => void;
}

export default class BioExamResultsEditionFormComponent extends CrudJournalEventBaseComponent<
  BioExamResultsReceivedJournalEvent,
  Args
> {
  styles = styles;

  @service declare router: RouterService;

  bioExamResults = bioExamResultsFilter(
    this.patient,
    this.patient.therapeuticalArea?.defaultBioExams
  );

  autoSaveDraft = true;

  @service declare session: SemeiaSessionService;

  @tracked
  isInvalid = false;

  static fullComponentId = "bio-exam-results/edition-form";
  static forcDisplayName = "Résultats biologiques";

  static journalEventClass = BioExamResultsReceivedJournalEvent;

  // eslint-disable-next-line parog-web/no-use-of-model-in-crud-forc
  retrieveJournalEvent = () => this.model;

  get groupedBioExamsObs(): ExamsByGroup {
    const sortedBioExams = this.bioExamResults
      .map(res => res.bioExamObservation as BioExamObservation)
      .sort(function (bioA, bioB) {
        return bioA.rank - bioB.rank;
      });

    return getGroupedBioExams(sortedBioExams);
  }

  get toastMessage(): ToastMessage | null {
    return this.session.patient
      ? {
          message: "Vos résultats ont été envoyés.",
          title: "Résultats de biologie"
        }
      : super.toastMessage;
  }

  get isDirty() {
    if (this.changeset.isInvalid) return false;

    return super.isDirty;
  }

  get model(): BioExamResultsReceivedJournalEvent {
    // eslint-disable-next-line parog-web/no-use-of-model-in-crud-forc
    return this.args.model;
  }

  get changeset() {
    this.draft = new DraftManager(this);
    return this.args.changeset;
  }

  get patient() {
    return this.args.patient;
  }

  async save(): Promise<void> {
    this.changeset.set("normalized", true);

    await super.save();
  }

  @action
  onFileUploadClicked(): void {
    if (this.session.medic) {
      ($(".sidebar") as HTMLSidebarElement).sidebar("show");
      this.router.transitionTo(this.router.currentRouteName, {
        queryParams: {
          tab: "upload-document"
        }
      });
    } else {
      this.router.transitionTo(
        `patient-web.protected.feature.component`,
        "document",
        "edition-form",
        {
          queryParams: {
            callbackUrl: "/patient-web/bio-results/edition"
          }
        }
      );
    }
  }
}
